export const normalize = (str: string = "") => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

export const middleEllipsis = (
  str: string,
  maxLength: number = 10,
  ellipsis: string = "...",
) => {
  if (str.length <= maxLength) {
    return str;
  }
  const ellipsisLength = ellipsis.length;
  const halfMaxLength = Math.floor((maxLength - ellipsisLength) / 2);
  const firstHalf = str?.toString().slice(0, halfMaxLength);
  const secondHalf = str?.toString().slice(str.length - halfMaxLength);

  return `${firstHalf}${ellipsis}${secondHalf}`;
};

export const MAX_ELLIPSIS = 70;
export const endEllipsis = (
  str: string,
  max: number = 10,
  ellipsis = "...",
): string => {
  return max <= ellipsis.length
    ? ellipsis.slice(0, max)
    : str.length <= max
      ? str
      : str.slice(0, max - ellipsis.length) + ellipsis;
};

/**
 * Convert any string into a clean, URL-friendly slug.
 * Removes accents, converts to lowercase, removes punctuation,
 * and collapses dashes.
 * Example: "Ô Jésus, mon Sauveur !" -> "o-jesus-mon-sauveur"
 */
export const slugify = (text: string = ""): string => {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/['’]/g, "-")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
};

/**
 * Returns formatted number-slug for hymns: e.g. "74-grand-dieu-nous-te-benissons"
 * If title is not available, returns just the number e.g. "74"
 */
export const getHymnSlug = (
  number: number | string,
  title?: string,
): string => {
  const cleanTitle = title ? slugify(title) : "";
  return cleanTitle ? `${number}-${cleanTitle}` : String(number);
};

export const LANGUAGE_LABELS: Record<string, string> = {
  ht: "Kreyòl",
  fr: "Français",
  es: "Español",
  en: "English",
};

/**
 * Returns the human-readable language label for a given language code (e.g., "ht" -> "Kreyòl").
 */
export const getLanguageLabel = (lang?: string): string => {
  if (!lang) return "";
  if (lang.includes(",")) {
    return lang
      .split(",")
      .map((l) => LANGUAGE_LABELS[l.trim().toLowerCase()] || l.trim())
      .filter(Boolean)
      .join(" & ");
  }
  return LANGUAGE_LABELS[lang.toLowerCase()] || lang;
};

export interface HymnVerseLike {
  number?: number | null;
  type?: string;
  order?: number;
  content?: string;
  text?: string;
  body?: string;
  lyrics?: string;
  verse_text?: string;
  lines?: string[];
  [key: string]: any;
}

export interface HymnFormatInput {
  number?: number | string | null;
  title?: string;
  section?: string;
  section_code?: string;
  category?: string | null;
  language?: string;
  verses?: HymnVerseLike[] | string[] | string | any;
  full_text?: string;
  source_data?: string | null;
}

export interface FormatLyricsOptions {
  sourceUrl?: string;
  includeHeader?: boolean;
  includeSource?: boolean;
}

/**
 * Extracts clean lines of lyrics from any verse representation.
 * Handles arrays of lines, raw text strings, and strips redundant
 * verse headers/numbers (digits, Roman numerals like I, II, III, ordinals,
 * and markers like "Couplet 1", "Refrain") so that UI badges and
 * verse numbers do not appear twice.
 */
export const extractVerseLines = (v: any): string[] => {
  if (!v) return [];

  let rawLines: string[] = [];

  // If already array of lines
  if (Array.isArray(v.lines) && v.lines.length > 0) {
    rawLines = v.lines
      .map((l: unknown) => (l != null ? String(l).trim() : ""))
      .filter((l: string) => l.length > 0);
  } else {
    // If raw string or inside content/text/body
    const rawText =
      typeof v === "string"
        ? v
        : v.content || v.text || v.body || v.lyrics || v.verse_text || "";

    if (!rawText || typeof rawText !== "string") return [];

    rawLines = rawText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
  }

  if (rawLines.length === 0) return [];

  // Helper to test if a line is solely a redundant stanza number/marker
  const isStandaloneHeader = (line: string): boolean => {
    const trimmed = line.trim();
    if (!trimmed) return false;

    // Digits or Roman numerals alone or with punctuation: "1", "1.", "1-", "1:", "1)", "I", "I.", "I-", "I:", "I)", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", etc.
    if (/^(?:[0-9]+|[ivxlcdm]+)[\.\)\-:\–\—]*$/i.test(trimmed)) {
      return true;
    }

    // Ordinals: "1er", "1ère", "1ere", "2ème", "2eme", "1st", "2nd", "3rd", etc.
    if (
      /^[0-9]+(?:er|ère|ere|ème|eme|th|st|nd|rd)[\.\)\-:\–\—]*$/i.test(trimmed)
    ) {
      return true;
    }

    // Named stanza / chorus headers: "Couplet 1", "Couplet I", "Strophe 1", "Strophe I", "Verse 1", "Kè", "Refrain", "Choeur", "Chorus", etc.
    if (
      /^(?:refrain|kè|choeur|chorus|couplet|strophe|verse|stanza)\b(?:\s*(?:[0-9]+|[ivxlcdm]+|(?:[0-9]+(?:er|ère|ere|ème|eme|th|st|nd|rd))))?[\.\)\-:\–\—]*$/i.test(
        trimmed,
      ) ||
      /^(?:chant)\s+(?:[0-9]+|[ivxlcdm]+|(?:[0-9]+(?:er|ère|ere|ème|eme|th|st|nd|rd)))[\.\)\-:\–\—]*$/i.test(
        trimmed,
      )
    ) {
      return true;
    }

    return false;
  };

  // Strip leading redundant stanza header lines while there are other lines remaining
  while (rawLines.length > 1 && isStandaloneHeader(rawLines[0])) {
    rawLines.shift();
  }

  if (rawLines.length === 0) return [];

  // If the first line starts with a number, Roman numeral, or section marker followed by the actual text:
  // e.g. "I - Lè'n rete...", "1. Grand Dieu...", "Refrain: Béni soit..."
  const line0 = rawLines[0];
  const cleanedLeadingMarker = line0
    .replace(/^(?:[0-9]+|[ivxlcdm]+)[\.\)\-:\–\—]+\s*/i, "")
    .replace(/^[0-9]+(?:er|ère|ere|ème|eme|th|st|nd|rd)[\.\)\-:\–\—]*\s+/i, "")
    .replace(
      /^(?:refrain|kè|choeur|chorus|couplet|strophe|verse|stanza|chant)\s+(?:[0-9]+|[ivxlcdm]+\b|(?:[0-9]+(?:er|ère|ere|ème|eme|th|st|nd|rd)))\s*[\.\)\-:\–\—]*\s*/i,
      "",
    )
    .replace(
      /^(?:refrain|kè|choeur|chorus|couplet|strophe|verse|stanza|chant)\s*[\.\)\-:\–\—]+\s*/i,
      "",
    );

  if (cleanedLeadingMarker && cleanedLeadingMarker !== line0) {
    rawLines[0] = cleanedLeadingMarker.trim();
  }

  return rawLines.filter((l) => l.length > 0);
};

/**
 * Safely parses verses data whether it is an array of objects, an array of strings,
 * a JSON-encoded string, or a fallback to full_text paragraphs.
 */
export const parseHymnVerses = (
  rawVerses: any,
  fullText?: string,
): HymnVerseLike[] => {
  let verses: any = rawVerses;

  if (typeof verses === "string") {
    try {
      verses = JSON.parse(verses);
    } catch {
      verses = null;
    }
  }

  if (Array.isArray(verses) && verses.length > 0) {
    return verses.map((v, index) => {
      if (typeof v === "string") {
        return { type: "verse", number: index + 1, content: v };
      }
      return v;
    });
  }

  // Fallback: parse full_text by paragraphs
  if (fullText && typeof fullText === "string") {
    const paragraphs = fullText
      .split(/\r?\n\s*\r?\n/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    let vIdx = 1;
    return paragraphs.map((para) => {
      const isChorus = /^(kè|refrain|choeur|chorus)/i.test(para);
      if (isChorus) {
        return {
          type: "chorus",
          content: para.replace(/^(kè|refrain|choeur|chorus)\s*:?\s*/i, ""),
        };
      }
      return {
        type: "verse",
        number: vIdx++,
        content: para,
      };
    });
  }

  return [];
};

/**
 * Formats a hymn's lyrics into a clean, well-structured, human-readable
 * text suitable for sharing, church presentation software, or clipboard copy.
 */
export const formatHymnLyrics = (
  item: HymnFormatInput,
  options: FormatLyricsOptions = {},
): string => {
  const { includeHeader = true, includeSource = true, sourceUrl } = options;

  const numStr = item.number != null ? `N° ${item.number}` : "";
  const titleStr = item.title || "Cantique";
  const displayTitle = numStr ? `${numStr} - ${titleStr}` : titleStr;
  const sectionName = item.section || item.section_code || "Chants d'Espérance";

  const isCreole =
    item.language === "ht" ||
    (item.section && /krey|créole/i.test(item.section));

  const verses = parseHymnVerses(item.verses, item.full_text);

  const blocks: string[] = [];
  let verseIndex = 1;
  let chorusIndex = 1;

  verses.forEach((v) => {
    const rawType = (v.type || "").toLowerCase();
    const rawContent =
      typeof v === "string"
        ? v
        : v.content ||
          v.text ||
          (Array.isArray(v.lines) ? v.lines.join("\n") : "");

    const isChorus =
      rawType === "chorus" ||
      rawType === "refrain" ||
      rawType === "kè" ||
      rawType === "choeur" ||
      /^(kè|refrain|choeur|chorus)/i.test(rawContent.trim());

    let header = "";

    if (isChorus) {
      const chorusLabel = isCreole ? "Kè" : "Refrain";
      header =
        chorusIndex > 1
          ? `[${chorusLabel} ${chorusIndex}]`
          : `[${chorusLabel}]`;
      chorusIndex++;
    } else if (rawType === "bridge" || rawType === "pont") {
      header = "[Pont]";
    } else if (
      rawType === "tag" ||
      rawType === "ending" ||
      rawType === "fin" ||
      rawType === "coda"
    ) {
      header = "[Fin]";
    } else {
      const vNum = v.number != null ? v.number : verseIndex++;
      header = `[V${vNum}]`;
    }

    const lines = extractVerseLines(v);
    if (lines.length > 0) {
      blocks.push(`${header}\n${lines.join("\n")}`);
    }
  });

  const parts: string[] = [];

  if (includeHeader) {
    parts.push(displayTitle);
    parts.push(`Recueil : ${sectionName}`);
    if (item.category) {
      parts.push(`Catégorie : ${item.category}`);
    }
    parts.push(""); // blank line before lyrics
  }

  if (blocks.length > 0) {
    parts.push(blocks.join("\n\n"));
  } else if (item.full_text) {
    parts.push(item.full_text.trim());
  }

  if (includeSource) {
    parts.push("");
    if (item.source_data) {
      parts.push(`Source : ${item.source_data}`);
    }
    parts.push("Source : Église Garizim (egarizim.com)");
    if (sourceUrl) {
      parts.push(sourceUrl);
    }
  }

  return parts.join("\n").trim() + "\n";
};

/**
 * Reliable cross-browser clipboard copy with execCommand fallback.
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (!text) return false;

  // Try modern navigator.clipboard first
  if (navigator?.clipboard?.writeText && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn("navigator.clipboard failed, attempting fallback:", err);
    }
  }

  // Fallback using textarea + execCommand
  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "-9999px";
    textArea.style.opacity = "0";
    textArea.setAttribute("readonly", "");
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand("copy");
    document.body.removeChild(textArea);
    return successful;
  } catch (err) {
    console.error("execCommand fallback failed:", err);
    return false;
  }
};

export * from "./utils/exporters";

